import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Users, ImagePlus, Mail, CheckCircle2, ArrowRight, X, PartyPopper } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE } from '@/lib/api';
import { resizeImage } from '@/lib/imageResize';
import { toast } from 'sonner';
import ShareAppButton from '@/components/ShareAppButton';

/**
 * 4-step setup wizard for brand-new signups. Auto-advances when the user
 * completes each step's required action. Skippable at every step.
 *
 * Trigger: shown when `user.onboarding_completed === false` AND we haven't
 * already shown it in this session (sessionStorage).
 *
 * Steps:
 *   1. Welcome
 *   2. Create family (skipped if user already has family_id from invite flow)
 *   3. Upload first photo (skipped if user is a guest with no upload rights)
 *   4. Invite family + finish
 */
const SESSION_KEY = 'kinsy_onboarding_dismissed_session';

function StepDot({ active, done }) {
  return (
    <div
      className={`h-1.5 rounded-full transition-all ${
        active ? 'w-6 bg-teal-600' : done ? 'w-1.5 bg-teal-600' : 'w-1.5 bg-stone-200'
      }`}
    />
  );
}

export default function OnboardingWizard() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [familyName, setFamilyName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const photoInputRef = useRef(null);

  // Decide whether to open the wizard
  useEffect(() => {
    if (!user) return;
    if (user.onboarding_completed) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    setOpen(true);
    // If user already has a family (joined via invite), skip step 2
    if (user.family_id) setStep((s) => (s === 2 ? 3 : s));
  }, [user]);

  const finish = async (markServer = true) => {
    if (markServer) {
      try {
        await axios.post(`${API_BASE}/auth/onboarding-complete`, {}, { withCredentials: true });
        await refreshUser();
      } catch {
        /* non-critical */
      }
    }
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  const skip = async () => {
    await finish(true);
    toast('You can always revisit setup from Profile');
  };

  const totalSteps = 4;
  const advance = () => setStep((s) => Math.min(s + 1, totalSteps));

  // ----- Step actions -----
  const createFamily = async (e) => {
    e?.preventDefault?.();
    if (!familyName.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/family`, { name: familyName.trim() }, { withCredentials: true });
      await refreshUser();
      toast.success(`${familyName.trim()} family created!`);
      advance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create family');
    } finally {
      setBusy(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = '';
    if (!rawFile) return;
    if (!rawFile.type.startsWith('image/')) {
      toast.error('Please pick an image');
      return;
    }
    setBusy(true);
    try {
      const file = await resizeImage(rawFile);
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API_BASE}/upload`, form, { withCredentials: true });
      setPhotoUploaded(true);
      toast.success('Photo added!');
      setTimeout(advance, 700);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE}/family/invite`,
        { email: inviteEmail.trim(), role: 'member', access_level: 'full' },
        { withCredentials: true },
      );
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invite');
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && skip()}>
      <DialogContent
        className="max-w-md rounded-3xl border-0 p-0 overflow-hidden gap-0"
        data-testid="onboarding-wizard"
      >
        {/* Teal header — same on every step for continuity */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-700 px-6 py-6 text-white relative">
          <button
            type="button"
            onClick={skip}
            className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/90"
            aria-label="Skip onboarding"
            data-testid="onboarding-skip-btn"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <StepDot key={i} active={i + 1 === step} done={i + 1 < step} />
            ))}
          </div>
          {step === 1 && (
            <>
              <h2 className="text-2xl font-bold leading-tight">Welcome, {user.name?.split(' ')[0]} 👋</h2>
              <p className="text-sm text-white/90 mt-1 leading-relaxed">
                Let&apos;s bring your family closer — this takes about a minute.
              </p>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="text-2xl font-bold leading-tight">Create your private family space</h2>
              <p className="text-sm text-white/90 mt-1">Invitation-only. No ads. No strangers.</p>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="text-2xl font-bold leading-tight">Add your first family moment</h2>
              <p className="text-sm text-white/90 mt-1">A photo, a milestone, an everyday moment — start your family&apos;s story.</p>
            </>
          )}
          {step === 4 && (
            <>
              <h2 className="text-2xl font-bold leading-tight">Bring your family closer</h2>
              <p className="text-sm text-white/90 mt-1">Invite grandparents, siblings, cousins — the people who love your family.</p>
            </>
          )}
        </div>

        {/* Step body */}
        <div className="p-6">
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <ul className="space-y-3 mb-6">
                {[
                  { icon: Users, text: 'Create your private family space' },
                  { icon: ImagePlus, text: 'Add your first family moment' },
                  { icon: Mail, text: 'Invite grandparents and loved ones' },
                ].map((row, i) => {
                  const Icon = row.icon;
                  return (
                    <li key={i} className="flex items-center gap-3 text-sm text-stone-700">
                      <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-teal-600" />
                      </div>
                      {row.text}
                    </li>
                  );
                })}
              </ul>
              <Button
                onClick={() => setStep(user.family_id ? 3 : 2)}
                className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold"
                data-testid="onboarding-next-1"
              >
                Let&apos;s go
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={createFamily} data-testid="onboarding-step-2">
              <Label htmlFor="fname" className="text-sm text-stone-700">Family name</Label>
              <Input
                id="fname"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="The Knight Family"
                className="mt-1.5 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                autoFocus
                data-testid="onboarding-family-name"
              />
              <p className="text-xs text-stone-400 mt-2 mb-5">
                You can change this anytime in Family settings.
              </p>
              <Button
                type="submit"
                disabled={busy || !familyName.trim()}
                className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold"
                data-testid="onboarding-create-family"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create family
              </Button>
              <button
                type="button"
                onClick={skip}
                className="w-full text-center text-stone-400 text-sm mt-3 hover:text-stone-600"
              >
                Skip for now
              </button>
            </form>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handlePhotoSelect}
                data-testid="onboarding-photo-input"
              />
              {!photoUploaded ? (
                <>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={busy}
                    className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-teal-300 bg-teal-50/40 hover:bg-teal-50 transition flex flex-col items-center justify-center text-teal-700 disabled:opacity-60"
                    data-testid="onboarding-pick-photo"
                  >
                    {busy ? (
                      <Loader2 className="w-8 h-8 animate-spin" />
                    ) : (
                      <>
                        <ImagePlus className="w-9 h-9 mb-2" />
                        <span className="text-sm font-semibold">Pick a photo from your device</span>
                        <span className="text-xs text-teal-600/70 mt-1">JPG, PNG, HEIC — up to 50 MB</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={advance}
                    className="w-full text-center text-stone-400 text-sm mt-4 hover:text-stone-600"
                    data-testid="onboarding-skip-photo"
                  >
                    I&apos;ll add one later
                  </button>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-teal-50 mx-auto flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-7 h-7 text-teal-600" />
                  </div>
                  <p className="text-sm text-stone-700">Photo added — onto the last step!</p>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="onboarding-step-4">
              <form onSubmit={sendInvite} className="space-y-3">
                <div>
                  <Label htmlFor="iemail" className="text-sm text-stone-700">Email address</Label>
                  <Input
                    id="iemail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="grandma@example.com"
                    className="mt-1.5 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                    data-testid="onboarding-invite-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy || !inviteEmail.trim()}
                  variant="outline"
                  className="w-full h-10 rounded-full border-teal-200 text-teal-700 hover:bg-teal-50"
                  data-testid="onboarding-send-invite"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send invite
                </Button>
              </form>
              <div className="mt-5 p-3 rounded-xl bg-teal-50/60 border border-teal-100 flex items-start gap-2">
                <PartyPopper className="w-4 h-4 text-teal-700 shrink-0 mt-0.5" />
                <p className="text-xs text-teal-900 leading-relaxed">
                  You can invite more later from <strong>Family settings</strong> — even from a text message.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-200">
                <p className="text-xs text-stone-500 mb-2">Know another family who'd love Kinsy?</p>
                <ShareAppButton
                  variant="outline"
                  className="w-full h-10 rounded-full border-teal-200 text-teal-700 hover:bg-teal-50"
                  label="Share Kinsy via text"
                  testId="onboarding-share-app-button"
                />
              </div>
              <Button
                onClick={() => finish(true)}
                className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold mt-4"
                data-testid="onboarding-finish"
              >
                Finish setup
                <CheckCircle2 className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
